import { CUSHION_MS, TtsSilenceFiller } from './tts-silence-filler';
import { fmtRanges, READY_STATE_NAMES, tsMs } from './tts-debug';
import { isManagedMediaSource, pickMediaSourceCtor } from './media-source-compat';
import { signal } from '@angular/core';

// Cap so a network burst can't grow the queue without bound. Oldest wins
// eviction because live TTS prefers recency over completeness.
const MAX_QUEUE_DEPTH = 64;

// A large *finite* MediaSource.duration. Finite so iOS treats the stream as
// VOD and starts playback from the head (Infinity makes it seek to the live
// edge, skipping the start); large so the playhead never reaches it and the
// element never fires `ended` when it drains the buffer between utterances
// (auto duration tracks the buffered end, which makes every underrun look
// like end-of-stream and tears down the iOS audio session). 24h outlasts
// any read-along session.
const STREAM_DURATION_S = 24 * 60 * 60;

// How often to check whether the silence cushion needs topping up. Well
// under CUSHION_MS so a tick can never be the reason the buffer drains.
const FILL_TICK_MS = 50;

// Drop buffered audio further behind the playhead than this. Continuous
// silence is only ~1.2 kB/s, but iOS caps an audio SourceBuffer around
// 5.5 MB and a long read-along session would otherwise walk into it.
const EVICT_BEHIND_S = 30;

/**
 * Owns the HTMLAudioElement + (Managed)MediaSource half of TTS read-along:
 * element lifecycle, SourceBuffer append queue, and the playback-kick /
 * stall-recovery workarounds live playback needs on iOS Safari and Android
 * Chrome. Protocol concerns (WS subscribe state, codec negotiation, timing
 * instrumentation) stay in TtsAudioService.
 */
export class TtsMediaPipeline {
  public readonly playing = signal(false);

  private audioEl: HTMLAudioElement | null = null;
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private objectUrl: string | null = null;
  private mimeType: string | null = null;
  private pendingHeader: Uint8Array | null = null;
  private appendQueue: Uint8Array[] = [];
  private appending = false;
  private mediaSessionWired = false;

  // Tracks whether the audio element has fired `waiting` without a matching
  // `playing` since. Used to drive the stall-recovery seek in kickPlayback —
  // see the comment there for why this matters.
  private stalled = false;
  // Keeps the buffer from ever draining between utterances. See
  // TtsSilenceFiller for the measurements behind this.
  private readonly filler = new TtsSilenceFiller();
  private fillTimer: ReturnType<typeof setInterval> | null = null;
  private appendingFiller = false;
  private bufEndBeforeAppend = 0;
  // Guards against filling before the init segment has landed.
  private sawRealAppend = false;
  // False until the element has genuinely begun playing. While false,
  // kickPlayback pins the playhead to the buffer start so a mobile
  // live-edge seek can't skip the head of the utterance.
  private started = false;

  constructor(private readonly document: Document) {}

  /** Snapshot of audio + buffer state, formatted for a single log line. */
  snap(): string {
    const a = this.audioEl;
    const sb = this.sourceBuffer;
    const ms = this.mediaSource;
    const ready = a ? `${a.readyState}(${READY_STATE_NAMES[a.readyState]})` : 'no-audio';
    const ct = a ? a.currentTime.toFixed(3) : '-';
    const paused = a ? a.paused : '-';
    const muted = a ? a.muted : '-';
    const err = a?.error ? `err=${a.error.code}` : '';
    const msState = ms?.readyState ?? 'no-ms';
    let buf = 'no-sb';
    if (sb) {
      const ranges: string[] = [];
      for (let i = 0; i < sb.buffered.length; i++) {
        ranges.push(`[${sb.buffered.start(i).toFixed(3)}, ${sb.buffered.end(i).toFixed(3)}]`);
      }
      buf = `${ranges.join(',') || 'empty'} updating=${sb.updating}`;
    }
    // `played` = ranges the element has actually rendered to output, and
    // `abuf` = the element's own view of buffered data (can diverge from the
    // SourceBuffer's on iOS). If `played` starts well above 0 while audio was
    // buffered from 0, the start was decoded-but-never-rendered (the clip).
    const played = a ? fmtRanges(a.played) : '-';
    const abuf = a ? fmtRanges(a.buffered) : '-';
    return `ms=${msState} ready=${ready} ct=${ct} paused=${paused} muted=${muted} ${err} buf=${buf} played=${played} abuf=${abuf} q=${this.appendQueue.length}`;
  }

  /**
   * Creates the audio element + MediaSource if they don't exist yet.
   * Returns false when no (Managed)MediaSource implementation is available,
   * so the caller can mark TTS unsupported.
   */
  ensure(): boolean {
    if (this.audioEl) return true;
    const MSCtor = pickMediaSourceCtor();
    if (!MSCtor) {
      return false;
    }

    const audio = this.document.createElement('audio');
    audio.preload = 'auto';
    audio.hidden = true;
    // `canplay` fires when the element first reaches HAVE_FUTURE_DATA.
    // Belt-and-braces against the gesture-bound play() having settled
    // before any media was buffered: when the element finally has data,
    // retry play() so it actually starts emitting sound.
    audio.addEventListener('canplay', () => {
      console.log(`[TTS ${tsMs()}] event canplay ${this.snap()}`);
      audio.play().then(
        () => console.log(`[TTS ${tsMs()}] play() ok (from canplay) ${this.snap()}`),
        (err) => console.log(`[TTS ${tsMs()}] play() reject (from canplay) ${err?.name}: ${err?.message}`),
      );
    });
    audio.addEventListener('waiting', () => {
      console.log(`[TTS ${tsMs()}] event waiting ${this.snap()}`);
      this.stalled = true;
      this.topUp();
    });
    // The cushion is maintained from media events, not from the fill timer:
    // WebKit clamps repeating timers to 1s in a backgrounded page, which is
    // exactly the screen-off case the cushion exists to protect. `timeupdate`
    // is driven by the media clock and keeps firing (~4/s) while audio plays,
    // and each append's `updateend` chains the next one, so one timeupdate
    // refills the whole cushion.
    audio.addEventListener('timeupdate', () => this.topUp());
    audio.addEventListener('playing', () => {
      console.log(`[TTS ${tsMs()}] event playing ${this.snap()}`);
      this.stalled = false;
      this.started = true;
    });
    for (const evt of [
      'loadstart', 'loadedmetadata', 'loadeddata', 'canplaythrough',
      'play', 'pause', 'stalled', 'suspend',
      'emptied', 'ended', 'error', 'ratechange', 'seeking', 'seeked',
    ]) {
      audio.addEventListener(evt, () => {
        console.log(`[TTS ${tsMs()}] event ${evt} ${this.snap()}`);
      });
    }
    // ManagedMediaSource only activates on iOS if AirPlay is disabled (or an
    // <source> HLS alternative is offered). We have no HLS fallback for live
    // TTS, so opt in by disabling remote playback. No-op outside iOS Safari.
    if ('disableRemotePlayback' in audio) {
      audio.disableRemotePlayback = true;
    }
    this.document.body.appendChild(audio);

    const ms = new MSCtor();
    if (isManagedMediaSource(MSCtor)) {
      audio.srcObject = ms as unknown as MediaProvider;
    } else {
      this.objectUrl = URL.createObjectURL(ms);
      audio.src = this.objectUrl;
    }

    ms.addEventListener('sourceopen', () => {
      console.log(`[TTS ${tsMs()}] ms sourceopen ${this.snap()}`);
      this.handleSourceOpen();
    });
    ms.addEventListener('sourceended', () => {
      console.log(`[TTS ${tsMs()}] ms sourceended ${this.snap()}`);
      this.playing.set(false);
    });
    ms.addEventListener('sourceclose', () => {
      console.log(`[TTS ${tsMs()}] ms sourceclose ${this.snap()}`);
    });
    for (const evt of ['startstreaming', 'endstreaming']) {
      ms.addEventListener(evt, () => {
        console.log(`[TTS ${tsMs()}] ms ${evt} ${this.snap()}`);
      });
    }

    this.audioEl = audio;
    this.mediaSource = ms;

    console.log(`[TTS ${tsMs()}] pipeline created managed=${isManagedMediaSource(MSCtor)} ${this.snap()}`);

    this.wireMediaSession();
    this.tryStartPlayback();
    return true;
  }

  teardown(): void {
    if (this.sourceBuffer && this.mediaSource?.readyState === 'open') {
      try {
        this.mediaSource.endOfStream();
      } catch {
        /* ignore */
      }
    }
    this.sourceBuffer = null;
    this.mediaSource = null;
    this.appendQueue = [];
    this.appending = false;
    this.pendingHeader = null;
    this.mimeType = null;
    this.stalled = false;
    this.started = false;
    this.stopFiller();
    this.filler.reset();
    this.appendingFiller = false;
    this.sawRealAppend = false;

    const audio = this.audioEl;
    if (audio) {
      try {
        audio.pause();
        if (audio.srcObject) audio.srcObject = null;
        if (this.objectUrl) {
          URL.revokeObjectURL(this.objectUrl);
          this.objectUrl = null;
        }
        audio.removeAttribute('src');
        audio.load();
        audio.remove();
      } catch {
        /* ignore */
      }
    }
    this.audioEl = null;
    this.playing.set(false);
  }

  /**
   * Accepts a decoded fMP4 init segment (ftyp + moov) for the given MIME.
   * Codec negotiation already happened in the service — this only routes the
   * header into the right buffering state.
   */
  acceptInit(mime: string, header: Uint8Array): void {
    this.mimeType = mime;
    if (this.mediaSource?.readyState === 'open' && !this.sourceBuffer) {
      console.log(`[TTS ${tsMs()}] init: create sb (ms open, no sb)`);
      this.createSourceBuffer(mime);
      this.appendQueue.unshift(header);
      if (!this.appending) this.pumpQueue();
    } else if (this.sourceBuffer) {
      console.log(`[TTS ${tsMs()}] init: mid-stream re-init (same codec)`);
      this.appendQueue = [header];
      if (!this.appending) this.pumpQueue();
    } else {
      console.log(`[TTS ${tsMs()}] init: stashing header until sourceopen`);
      this.pendingHeader = header;
    }
  }

  /** Accepts one fMP4 media segment (moof + mdat). */
  acceptFrame(chunk: Uint8Array, frameNumber: number): void {
    console.log(`[TTS ${tsMs()}] frame #${frameNumber} size=${chunk.byteLength} sb=${!!this.sourceBuffer} ${this.snap()}`);
    if (!this.sourceBuffer) {
      this.queuePush(chunk);
      return;
    }
    this.enqueueAppend(chunk);
  }

  /** Visibilitychange recovery: restart the element if backgrounding paused it. */
  resumeIfPaused(): void {
    if (this.audioEl?.paused) {
      this.audioEl.play().catch(() => {});
    }
  }

  private handleSourceOpen(): void {
    if (!this.mediaSource) return;
    // Large finite duration — see STREAM_DURATION_S. NOT Infinity (iOS would
    // seek to the live edge and skip the start) and NOT left to auto-track
    // the buffered end (iOS would fire `ended` on every underrun). The setter
    // only raises duration, and we never call endOfStream mid-stream, so this
    // sticks across appends.
    try {
      this.mediaSource.duration = STREAM_DURATION_S;
    } catch {
      /* setter can throw if not open / a buffer is updating; ignore */
    }
    if (!this.mimeType || !this.pendingHeader) return;
    this.createSourceBuffer(this.mimeType);
    // The header must precede any frames that arrived while we were waiting
    // for `sourceopen` — unshift, not enqueueAppend.
    this.appendQueue.unshift(this.pendingHeader);
    this.pendingHeader = null;
    if (!this.appending) this.pumpQueue();
  }

  private createSourceBuffer(mime: string): void {
    if (!this.mediaSource) return;
    try {
      const sb = this.mediaSource.addSourceBuffer(mime);
      // 'sequence' makes timestamps inferred from append order — what live
      // streaming wants when producers can't guarantee monotonic timestamps.
      sb.mode = 'sequence';
      sb.addEventListener('updateend', () => {
        this.appending = false;
        const wasFiller = this.appendingFiller;
        if (wasFiller) {
          this.appendingFiller = false;
          const end = sb.buffered.length ? sb.buffered.end(sb.buffered.length - 1) : 0;
          // WebKit ignores a byte-identical append; catching that here is what
          // lets the filler stand down instead of appending into the void.
          this.filler.noteAppendGrew(end > this.bufEndBeforeAppend + 0.001);
        } else {
          this.sawRealAppend = true;
          console.log(`[TTS ${tsMs()}] sb updateend ${this.snap()}`);
        }
        // play() must be retried *after* the append commits — calling it
        // synchronously after appendBuffer races the browser's readiness
        // check against an empty buffer and the element decides to wait.
        // Cushion top-ups land several times a second and need no kick, so
        // they only get one when playback is actually stalled. Otherwise the
        // kick logs and its play() calls would drown the diagnostic log.
        if (!wasFiller || this.stalled) this.kickPlayback();
        this.pumpQueue();
        // Chain the cushion: each append re-checks, so a single trigger fills
        // all the way up to CUSHION_MS without waiting on the timer.
        this.topUp();
      });
      sb.addEventListener('error', (e) => {
        console.error('[TTS] SourceBuffer error', e, this.snap());
        this.appending = false;
      });
      sb.addEventListener('abort', () => {
        console.log(`[TTS ${tsMs()}] sb abort ${this.snap()}`);
      });
      // ManagedMediaSource may evict buffered ranges at any time. We don't
      // refetch (producer is realtime — past audio is past), but we track
      // when the buffer has been fully cleared.
      sb.addEventListener('bufferedchange', () => {
        console.log(`[TTS ${tsMs()}] sb bufferedchange ${this.snap()}`);
        if (sb.buffered.length === 0) this.playing.set(false);
      });
      console.log(`[TTS ${tsMs()}] sb created mime=${mime} mode=${sb.mode}`);
      this.sourceBuffer = sb;
      this.startFiller();
    } catch (err) {
      console.error('[TTS] addSourceBuffer failed', err);
    }
  }

  private queuePush(chunk: Uint8Array): void {
    if (this.appendQueue.length >= MAX_QUEUE_DEPTH) {
      this.appendQueue.shift();
    }
    this.appendQueue.push(chunk);
  }

  private enqueueAppend(chunk: Uint8Array): void {
    this.queuePush(chunk);
    if (!this.appending) this.pumpQueue();
  }

  private pumpQueue(): void {
    if (this.appending) return;
    const sb = this.sourceBuffer;
    const audio = this.audioEl;
    if (!sb || sb.updating) return;
    // Appending to a SourceBuffer whose element is in error state throws.
    if (audio?.error) {
      console.warn('[TTS] audio element in error state; dropping queue', audio.error);
      this.appendQueue = [];
      return;
    }
    const next = this.appendQueue.shift();
    if (!next) return;
    this.appending = true;
    // Filler appends are frequent and carry no information, so they stay out
    // of the log and out of the `playing` signal.
    const isFiller = this.filler.isFiller(next);
    this.appendingFiller = isFiller;
    this.bufEndBeforeAppend = sb.buffered.length ? sb.buffered.end(sb.buffered.length - 1) : 0;
    if (!isFiller) {
      console.log(`[TTS ${tsMs()}] appendBuffer size=${next.byteLength} ${this.snap()}`);
    }
    try {
      sb.appendBuffer(next);
      if (!isFiller && !this.playing()) this.playing.set(true);
    } catch (err) {
      this.appending = false;
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        this.appendQueue.unshift(next);
        this.evictOldest();
        return;
      }
      console.error('[TTS] appendBuffer failed', err);
    }
  }

  /** Seconds of audio buffered ahead of the playhead. */
  private bufferedAhead(): number {
    const sb = this.sourceBuffer;
    const audio = this.audioEl;
    if (!sb || !audio || sb.buffered.length === 0) return 0;
    return Math.max(0, sb.buffered.end(sb.buffered.length - 1) - audio.currentTime);
  }

  private startFiller(): void {
    if (this.fillTimer !== null) return;
    this.fillTimer = setInterval(() => this.topUp(), FILL_TICK_MS);
  }

  private stopFiller(): void {
    if (this.fillTimer === null) return;
    clearInterval(this.fillTimer);
    this.fillTimer = null;
  }

  /**
   * Tops the silence cushion back up to CUSHION_MS. Real audio always wins:
   * this only appends when the queue is empty, so a sentence never waits
   * behind filler. The cushion is the reason the element never fires
   * `waiting` between utterances, which is what stops the first word being
   * clipped on Bluetooth.
   */
  private topUp(): void {
    const sb = this.sourceBuffer;
    const audio = this.audioEl;
    if (!sb || !audio || sb.updating || this.appending) return;
    if (!this.filler.enabled) {
      this.stopFiller();
      return;
    }
    if (audio.error || !this.sawRealAppend) return;
    // Only meaningful once playback is genuinely under way: the cushion
    // exists to stop the buffer draining *between* utterances, and before
    // the first `playing` there is nothing to drain.
    if (!this.started || audio.paused) return;
    // Real segments queued or in flight: leave the buffer to them.
    if (this.appendQueue.length > 0) return;
    if (sb.buffered.length > 0 && audio.currentTime - sb.buffered.start(0) > EVICT_BEHIND_S) {
      this.evictOldest();
      return;
    }
    if (this.bufferedAhead() * 1000 >= CUSHION_MS) return;
    const frag = this.filler.next();
    if (frag) this.enqueueAppend(frag);
  }

  private evictOldest(): void {
    const sb = this.sourceBuffer;
    const audio = this.audioEl;
    if (!sb || sb.buffered.length === 0 || !audio) return;
    const start = sb.buffered.start(0);
    const end = Math.min((audio.currentTime || 0) - 1, sb.buffered.end(0));
    if (end <= start) return;
    try {
      sb.remove(start, end);
    } catch (err) {
      console.warn('[TTS] eviction failed', err);
    }
  }

  private tryStartPlayback(): void {
    const audio = this.audioEl;
    if (!audio) return;
    if (!audio.paused) {
      console.log(`[TTS ${tsMs()}] tryStartPlayback skip (already not-paused) ${this.snap()}`);
      return;
    }
    console.log(`[TTS ${tsMs()}] tryStartPlayback calling play() ${this.snap()}`);
    audio.play().then(
      () => console.log(`[TTS ${tsMs()}] tryStartPlayback play() ok ${this.snap()}`),
      (err) => console.warn(`[TTS ${tsMs()}] tryStartPlayback play() reject ${err?.name}: ${err?.message}`),
    );
  }

  /**
   * Called after each successful append commits. Two failure modes to
   * defend against:
   *   1. In `sequence` mode the first segment lands at the SourceBuffer's
   *      `timestampOffset`, which can be > 0 (e.g. after a mid-stream
   *      re-init), leaving `audio.currentTime` stranded before the
   *      buffered range — the element then waits silently for data
   *      ahead of currentTime that already exists, just offset wrong.
   *   2. The gesture-bound `play()` in ensure flips `paused` to false
   *      synchronously before any media is buffered, so we can't gate the
   *      retry on `paused`. Calling `play()` while already playing is a
   *      documented no-op, so call it unconditionally — this is what
   *      actually unsticks the element when the initial play promise
   *      settled before data was available.
   */
  private kickPlayback(): void {
    const audio = this.audioEl;
    const sb = this.sourceBuffer;
    if (!audio || !sb || sb.buffered.length === 0) {
      console.log(`[TTS ${tsMs()}] kickPlayback skip (no audio/sb/buffer) ${this.snap()}`);
      return;
    }
    const bufferedStart = sb.buffered.start(0);
    const bufferedEnd = sb.buffered.end(sb.buffered.length - 1);
    if (!this.started && audio.currentTime > bufferedStart + 0.5) {
      // Mobile browsers can seek to the live edge on the first play(),
      // skipping the buffered head so the listener misses the start of the
      // utterance. Until playback genuinely begins (the `playing` event
      // sets `started`), pin the playhead back to the buffer start. The
      // 0.5s guard ignores small natural progress so we don't fight a
      // playback start that's already underway.
      console.log(`[TTS ${tsMs()}] kickPlayback pin-to-start ct ${audio.currentTime.toFixed(3)} -> ${bufferedStart.toFixed(3)}`);
      try {
        audio.currentTime = bufferedStart;
      } catch (err) {
        console.warn(`[TTS ${tsMs()}] kickPlayback pin-to-start threw`, err);
      }
    } else if (audio.currentTime < bufferedStart) {
      console.log(`[TTS ${tsMs()}] kickPlayback seek ct ${audio.currentTime.toFixed(3)} -> ${bufferedStart.toFixed(3)}`);
      try {
        audio.currentTime = bufferedStart;
      } catch (err) {
        console.warn(`[TTS ${tsMs()}] kickPlayback seek threw`, err);
      }
    } else if (this.stalled && !audio.seeking && audio.currentTime + 0.001 < bufferedEnd) {
      // After a `waiting`, Chrome holds in HAVE_CURRENT_DATA until ~3s of
      // media is buffered ahead — much higher than the ~0.5s threshold it
      // uses for initial playback. So an utterance can sit fully buffered
      // and silent until enough later utterances accumulate to cross the
      // rebuffer threshold (we've observed 2s ahead being insufficient).
      // A tiny forward seek resets the readyState machine: the browser
      // treats it as a fresh start and resumes from the lower threshold.
      const target = audio.currentTime + 0.001;
      console.log(`[TTS ${tsMs()}] kickPlayback stall-recovery seek ct ${audio.currentTime.toFixed(3)} -> ${target.toFixed(3)} (bufEnd=${bufferedEnd.toFixed(3)})`);
      try {
        audio.currentTime = target;
      } catch (err) {
        console.warn(`[TTS ${tsMs()}] kickPlayback stall-recovery seek threw`, err);
      }
    }
    console.log(`[TTS ${tsMs()}] kickPlayback calling play() ${this.snap()}`);
    audio.play().then(
      () => console.log(`[TTS ${tsMs()}] kickPlayback play() ok ${this.snap()}`),
      (err) => console.log(`[TTS ${tsMs()}] kickPlayback play() reject ${err?.name}: ${err?.message}`),
    );
  }

  private wireMediaSession(): void {
    if (this.mediaSessionWired || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Live translation',
        artist: 'Speaksee',
      });
      navigator.mediaSession.setActionHandler('play', () => {
        this.audioEl?.play().catch(() => {});
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        this.audioEl?.pause();
      });
      this.mediaSessionWired = true;
    } catch (err) {
      console.warn('[TTS] MediaSession wiring failed', err);
    }
  }
}
